import { useId, useMemo, useRef, useState, useCallback } from 'react';
import { useAnimationFrame } from '../viz/useAnimationFrame';
import {
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../viz/vizShared';

// Realistic SVG beaker glassware. Physical glass/liquid hex colors are kept;
// theme-driven accents (active glow, label) use --ws-* tokens.
//
// UPGRADE: true curved meniscus, animated surface ripple, rising gas bubbles,
// swirl (vortex) motion, settling precipitate, and an optional narration `cue`
// (progress → fill level, highlight tokens → swirl/bubbles/precipitate, play →
// run effects) forwarded by the lecture player. Self-demonstrates when no cue is
// present. All new props are OPTIONAL — existing call sites are unaffected.

export interface BeakerProps {
  size?: 250 | 500;
  fillLevel?: number; // 0-100 (% of capacity)
  fillColor?: string;
  muddy?: boolean;
  cleared?: boolean;
  precipitate?: number; // 0-1
  label?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  /** Animate swirling vortex motion of the liquid. */
  swirl?: boolean;
  /** Rising gas bubbles (effervescence / reaction). */
  bubbles?: boolean;
  /** Narration-sync cue forwarded by the lecture player. */
  cue?: VizCue;
}

export default function Beaker({
  size = 500,
  fillLevel = 0,
  fillColor = '#7DD3FC',
  muddy = false,
  cleared = false,
  precipitate = 0,
  label = '',
  onClick,
  active = false,
  disabled = false,
  className = '',
  swirl = false,
  bubbles = false,
  cue,
}: BeakerProps) {
  const id = useId();
  const reduced = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);
  const width = size === 500 ? 130 : 100;
  const height = size === 500 ? 170 : 130;

  // --- Cue derivation: progress drives fill; highlight tokens toggle effects.
  const tokens = cueHighlights(cue);
  const cueFill = cue?.progress != null ? Math.max(0, Math.min(100, cue.progress * 100)) : null;
  const effectiveFill = cueFill ?? fillLevel;
  const cueSwirl = cueOn ? tokens.includes('swirl') || tokens.includes('mix') || tokens.includes('stir') : false;
  const cueBubbles = cueOn ? tokens.includes('bubbles') || tokens.includes('gas') || tokens.includes('react') || tokens.includes('boil') : false;
  const cuePrecip = cueOn && (tokens.includes('precipitate') || tokens.includes('precip')) ? 0.8 : null;

  const playing = cue?.play !== false;
  const swirlOn = (swirl || cueSwirl) && !reduced && playing;
  const bubblesOn = (bubbles || cueBubbles) && effectiveFill > 0 && !reduced && playing;
  const effectivePrecip = cuePrecip ?? precipitate;

  const liquidColor = cleared ? '#B8E6F2' : muddy ? '#8B7355' : fillColor;

  const beakerLeft = 25;
  const beakerRight = 115;
  const beakerTop = 25;
  const beakerBottom = 145;
  const beakerWidth = beakerRight - beakerLeft;
  const cx = beakerLeft + beakerWidth / 2;

  const innerHeight = beakerBottom - beakerTop - 10;
  const fillHeight = (effectiveFill / 100) * innerHeight;
  const fillTop = beakerBottom - 5 - fillHeight;
  const precipHeight = effectivePrecip * 20;
  const liqRx = beakerWidth / 2 - 5;

  // --- Animated surface tilt (swirl) + ripple phase, mutated in a tiny rAF and
  // mirrored to a state tick so the SVG re-paints each frame.
  const animateAny = swirlOn || bubblesOn;
  const phaseRef = useRef({ t: 0, tilt: 0 });
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => (n + 1) % 1_000_000), []);

  useAnimationFrame((dt) => {
    phaseRef.current.t += dt;
    // Swirl tilts the surface back and forth like a vortex meniscus.
    phaseRef.current.tilt = swirlOn ? Math.sin(phaseRef.current.t * 4.2) * 5 : 0;
    bump();
  }, animateAny);

  const t = phaseRef.current.t;
  const tilt = phaseRef.current.tilt;
  // Gentle ripple even at rest amplitude scaled by swirl.
  const ripple = swirlOn ? 2.2 : 0.8;
  const surfL = fillTop + Math.sin(t * 2.6) * (swirlOn ? ripple : 0) - tilt;
  const surfR = fillTop - Math.sin(t * 2.6) * (swirlOn ? ripple : 0) + tilt;

  // Curved meniscus path (concave) across the liquid surface.
  const meniscusPath = `M${beakerLeft + 3} ${surfL}
    Q${cx} ${fillTop + (swirlOn ? 0 : 2.5)} ${beakerRight - 3} ${surfR}
    L${beakerRight - 3} ${beakerBottom - 5}
    L${beakerLeft + 3} ${beakerBottom - 5} Z`;

  const bubbleSeeds = useMemo(
    () => Array.from({ length: 9 }, (_, i) => ({
      x: 18 + ((i * 13) % (beakerWidth - 24)),
      r: 1 + (i % 3) * 0.7,
      speed: 0.7 + (i % 4) * 0.25,
      phase: (i / 9),
    })),
    [beakerWidth],
  );

  return (
    <svg
      viewBox="0 0 140 175"
      width={width}
      height={height}
      onClick={disabled ? undefined : onClick}
      role="img"
      aria-label={`Beaker${label ? ` ${label}` : ''}, ${Math.round(effectiveFill)}% full`}
      style={{
        cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease',
      }}
      className={className}
    >
      <defs>
        <linearGradient id={`glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(170,200,220,0.8)" />
          <stop offset="8%" stopColor="rgba(220,240,255,0.5)" />
          <stop offset="15%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="30%" stopColor="rgba(200,230,245,0.15)" />
          <stop offset="70%" stopColor="rgba(200,230,245,0.15)" />
          <stop offset="85%" stopColor="rgba(255,255,255,0.25)" />
          <stop offset="92%" stopColor="rgba(220,240,255,0.4)" />
          <stop offset="100%" stopColor="rgba(170,200,220,0.7)" />
        </linearGradient>
        <linearGradient id={`liquid-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={liquidColor} stopOpacity="0.78" />
          <stop offset="18%" stopColor={liquidColor} stopOpacity="0.9" />
          <stop offset="100%" stopColor={liquidColor} stopOpacity="0.97" />
        </linearGradient>
        {muddy && (
          <pattern id={`muddy-${id}`} patternUnits="userSpaceOnUse" width="10" height="10">
            <rect width="10" height="10" fill="#8B7355" />
            <circle cx="3" cy="3" r="1.2" fill="#6B5344" opacity="0.6" />
            <circle cx="8" cy="6" r="0.8" fill="#5B4334" opacity="0.5" />
            <circle cx="5" cy="9" r="1" fill="#7B6354" opacity="0.4" />
          </pattern>
        )}
        <linearGradient id={`precip-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#D4C8A8" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#8A7858" stopOpacity="0.95" />
        </linearGradient>
        <linearGradient id={`rim-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#A0C0D8" />
          <stop offset="50%" stopColor="#7A9BB8" />
          <stop offset="100%" stopColor="#5A7A95" />
        </linearGradient>
        <radialGradient id={`bottom-${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.4)" />
          <stop offset="100%" stopColor="rgba(150,180,200,0.6)" />
        </radialGradient>
        <filter id={`shadow-${id}`} x="-20%" y="-10%" width="140%" height="130%">
          <feDropShadow dx="3" dy="4" stdDeviation="3" floodOpacity="0.3" />
        </filter>
        <clipPath id={`liquid-clip-${id}`}>
          <rect
            x={beakerLeft + 3}
            y={beakerTop + 5}
            width={beakerWidth - 6}
            height={beakerBottom - beakerTop - 8}
          />
        </clipPath>
      </defs>

      {active && (
        <rect
          x={beakerLeft - 8}
          y={beakerTop - 8}
          width={beakerWidth + 16}
          height={beakerBottom - beakerTop + 16}
          rx="8"
          fill="none"
          stroke="var(--ws-accent)"
          strokeWidth="3"
        />
      )}

      <g filter={`url(#shadow-${id})`}>
        <rect
          x={beakerLeft}
          y={beakerTop}
          width={beakerWidth}
          height={beakerBottom - beakerTop}
          fill="rgba(180,200,220,0.1)"
          rx="2"
        />
        <line x1={beakerLeft} y1={beakerTop} x2={beakerLeft} y2={beakerBottom} stroke="#7A9BB8" strokeWidth="3" strokeLinecap="round" />
        <line x1={beakerRight} y1={beakerTop} x2={beakerRight} y2={beakerBottom} stroke="#7A9BB8" strokeWidth="3" strokeLinecap="round" />
        <path
          d={`M${beakerLeft} ${beakerBottom} Q${beakerLeft + beakerWidth / 2} ${beakerBottom + 8} ${beakerRight} ${beakerBottom}`}
          fill="none"
          stroke="#7A9BB8"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <ellipse cx={beakerLeft + beakerWidth / 2} cy={beakerBottom - 2} rx={beakerWidth / 2 - 3} ry="5" fill={`url(#bottom-${id})`} />
        <rect x={beakerLeft + 1} y={beakerTop + 1} width={beakerWidth - 2} height={beakerBottom - beakerTop - 1} fill={`url(#glass-${id})`} />
        <ellipse cx={beakerLeft + beakerWidth / 2} cy={beakerTop} rx={beakerWidth / 2 + 2} ry="4" fill={`url(#rim-${id})`} stroke="#6A8AA5" strokeWidth="1" />
        <path
          d={`M${beakerLeft - 2} ${beakerTop} Q${beakerLeft - 12} ${beakerTop - 2} ${beakerLeft - 10} ${beakerTop - 12} Q${beakerLeft - 8} ${beakerTop - 18} ${beakerLeft + 5} ${beakerTop - 10} Q${beakerLeft + 8} ${beakerTop - 5} ${beakerLeft + 5} ${beakerTop}`}
          fill={`url(#glass-${id})`}
          stroke="#7A9BB8"
          strokeWidth="2"
        />
      </g>

      {effectiveFill > 0 && (
        <g clipPath={`url(#liquid-clip-${id})`}>
          {/* Body of liquid with a true curved meniscus top. */}
          <path d={meniscusPath} fill={muddy && !cleared ? `url(#muddy-${id})` : `url(#liquid-${id})`} />
          {/* Concave meniscus rim highlight. */}
          <path
            d={`M${beakerLeft + 3} ${surfL} Q${cx} ${fillTop + (swirlOn ? 0 : 2.5)} ${beakerRight - 3} ${surfR}`}
            fill="none"
            stroke="rgba(255,255,255,0.55)"
            strokeWidth="1.2"
          />
          <ellipse cx={cx - 8} cy={fillTop + 1} rx="14" ry="1.8" fill="rgba(255,255,255,0.35)" />

          {/* Swirl vortex streaks. */}
          {swirlOn && (
            <g opacity="0.4" stroke="rgba(255,255,255,0.6)" fill="none" strokeWidth="1">
              {[0, 1, 2].map((k) => {
                const yy = fillTop + 8 + k * (fillHeight / 4);
                const amp = (liqRx - 4) * (1 - k * 0.18);
                const ph = t * 3 + k * 1.1;
                return (
                  <path
                    key={k}
                    d={`M${cx - amp} ${yy} Q${cx + Math.sin(ph) * amp} ${yy + 4} ${cx + amp} ${yy}`}
                  />
                );
              })}
            </g>
          )}

          {/* Rising gas bubbles. */}
          {bubblesOn && (
            <g>
              {bubbleSeeds.map((b, i) => {
                const span = fillHeight - 4;
                const prog = ((t * b.speed + b.phase) % 1);
                const by = beakerBottom - 6 - prog * span;
                const bx = beakerLeft + 3 + b.x + Math.sin(t * 3 + i) * 1.5;
                const op = Math.min(1, (1 - prog) * 1.4) * 0.75;
                return <circle key={i} cx={bx} cy={by} r={b.r} fill="rgba(255,255,255,0.85)" opacity={op} />;
              })}
            </g>
          )}

          {/* Suspended muddy particulate. */}
          {muddy && !cleared && (
            <g opacity="0.7">
              {Array.from({ length: 10 }).map((_, i) => (
                <circle key={i} cx={beakerLeft + 15 + (i % 4) * 20} cy={fillTop + 10 + Math.floor(i / 4) * 25} r={1.5 + (i % 3) * 0.5} fill="#4A3A2A">
                  <animate
                    attributeName="cy"
                    values={`${fillTop + 10 + Math.floor(i / 4) * 25};${fillTop + 15 + Math.floor(i / 4) * 25};${fillTop + 10 + Math.floor(i / 4) * 25}`}
                    dur={`${1.5 + (i % 3) * 0.5}s`}
                    repeatCount="indefinite"
                  />
                </circle>
              ))}
            </g>
          )}
        </g>
      )}

      {effectivePrecip > 0 && (
        <g clipPath={`url(#liquid-clip-${id})`}>
          <path
            d={`M${beakerLeft + 3} ${beakerBottom - 7 - precipHeight}
               Q${cx} ${beakerBottom - 7 - precipHeight - 3} ${beakerRight - 3} ${beakerBottom - 7 - precipHeight}
               L${beakerRight - 3} ${beakerBottom - 7} L${beakerLeft + 3} ${beakerBottom - 7} Z`}
            fill={`url(#precip-${id})`}
          />
          <ellipse cx={cx} cy={beakerBottom - 7 - precipHeight} rx={liqRx} ry="2" fill="#C4B896" opacity="0.7" />
        </g>
      )}

      <rect x={beakerLeft + 3} y={beakerTop + 8} width="4" height={beakerBottom - beakerTop - 20} fill="rgba(255,255,255,0.5)" rx="2" />
      <rect x={beakerLeft + 10} y={beakerTop + 15} width="2" height={beakerBottom - beakerTop - 35} fill="rgba(255,255,255,0.25)" rx="1" />

      {[50, 100, 150, 200, 250, ...(size === 500 ? [300, 400, 500] : [])]
        .filter((mark) => mark <= size)
        .map((mark) => {
          const markRatio = mark / size;
          const y = beakerBottom - 8 - markRatio * innerHeight;
          const isMain = mark % 100 === 0;
          return (
            <g key={mark}>
              <line x1={beakerRight - (isMain ? 12 : 8)} y1={y} x2={beakerRight - 3} y2={y} stroke="#5A7A95" strokeWidth={isMain ? 1.5 : 1} />
              {isMain && (
                <text x={beakerRight - 14} y={y + 3} fontSize="9" fill="#5A7A95" textAnchor="end" fontFamily="Arial, sans-serif">
                  {mark}
                </text>
              )}
            </g>
          );
        })}

      <text x={beakerRight - 8} y={beakerTop + 18} fontSize="8" fill="#5A7A95" textAnchor="end" fontFamily="Arial, sans-serif">
        mL
      </text>

      {label && (
        <text x={beakerLeft + beakerWidth / 2} y="166" fontSize="12" fill="var(--ws-ink)" textAnchor="middle" fontWeight="600">
          {label}
        </text>
      )}
    </svg>
  );
}
